<template>
  <div>
    <nav class="navbar">
      <div class="navbar-container">
        <a class="navbar-brand" href="#">請求管理システム</a>
        <div class="navbar-menu">
          <router-link class="nav-link" v-if="isLoggedIn" :to="{ name: 'Home'}">ホーム</router-link>

          <div
            v-for="(dropdown, index) in filteredDropdowns"
            :key="index"
            class="nav-item"
          >
            <span class="nav-link dropdown-toggle menu-dropdown-toggle">
              <span>{{ dropdown.label }}</span>
              <span
                v-if="dropdown.notificationKey === 'estimateApprovalPending' && approvalPendingCount > 0"
                class="approval-notification-badge"
                :aria-label="`承認待ち${approvalPendingCount}件`"
              >
                {{ approvalPendingBadgeText }}
              </span>
            </span>
            <div class="dropdown-menu">
              <router-link
                v-for="(item, itemIndex) in dropdown.items"
                :key="itemIndex"
                class="dropdown-item dropdown-item--menu-entry"
                :to="{ name: item.route, query: item.query }"
              >
                <span>{{ item.label }}</span>
                <span
                  v-if="item.notificationKey === 'estimateApprovalPending' && approvalPendingCount > 0"
                  class="approval-notification-badge"
                  :aria-label="`承認待ち${approvalPendingCount}件`"
                >
                  {{ approvalPendingBadgeText }}
                </span>
              </router-link>
            </div>
          </div>

          <div class="nav-item" v-if="isLoggedIn">
            <span class="nav-link dropdown-toggle">{{ userName }}様</span>
            <div class="dropdown-menu">
              <button class="dropdown-item" @click="handlePasswordUpdateClick(userId)">パスワード更新</button>
              <button class="dropdown-item" @click="handleLogout">ログアウト</button>
            </div>
          </div>
        </div>

        <div class="text-right text-nowrap header-actions">
          <button
            v-if="isLoggedIn && canAccessMeasureSystem"
            class="measure-link-button"
            type="button"
            :disabled="isSsoLoading"
            @click="moveToMeasureSystem"
          >
            {{ isSsoLoading ? '連携中...' : '空気環境システムへ' }}
          </button>
          <router-link v-if="!isLoggedIn" class="nav-link" :to="{ name: 'Login'}">ログイン</router-link>
        </div>
      </div>
    </nav>

    <!-- パスワード更新モーダル -->
    <PasswordResetHeaderModal
      v-if="isModalVisible"
      :userId="paramsIdToPasswordUpdate"
      @onClose="isModalVisible = false"
    />
  </div>
</template>

<script>
import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { logout, createSystemHandoff, getEstimateApprovalPendingCount } from '@/api/axios';
import { Modal } from 'bootstrap';
import PasswordResetHeaderModal from '@/components/shared/PasswordResetHeaderModal.vue';
import { SHOW_ESTIMATE_MENU } from '@/config/navigation';

const NO_ACCESS_ROLE = 3;
const APPROVAL_PENDING_REFRESH_INTERVAL_MS = 60 * 1000;

export default {
  name: "AppHeader",
  components: {
    PasswordResetHeaderModal
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const userId = localStorage.getItem('userId');
    const paramsIdToPasswordUpdate = ref(null);
    const isModalVisible = ref(false); // モーダルの表示/非表示を制御
    const isSsoLoading = ref(false);
    const approvalPendingCount = ref(0);
    let approvalPendingRefreshTimer = null;
    let isApprovalPendingCountLoading = false;
    let isApprovalPendingCountRefreshQueued = false;

    // ドロップダウン設定
    const dropdowns = ref([
      {
        label: 'マスタ管理',
        adminOnly: '',
        items: [
          { label: '顧客情報', route: 'CustomerList' },
          { label: '顧客担当者情報', route: 'CustomerRepresentativeList' },
          { label: '建物種別情報', route: 'BuildingTypeList' },
          { label: '建物情報', route: 'BuildingList' },
          { label: '業者情報', route: 'VendorList' },
          { label: '業務区分情報', route: 'BusinessClassificationList' },
          { label: 'ユーザ情報', route: 'UserList' },
        ],
      },
      {
        label: '集計情報管理',
        adminOnly: true,
        items: [
          { label: '年間予定作業 売上集計', route: 'SalesByData' },
          { label: '臨時作業 売上集計', route: 'TemporaryWorkSalesByData' },
          { label: '全作業 売上集計', route: 'TotalSalesByData' },
          { label: '業者作業履歴集計', route: 'VendorWorkHistory' },
        ],
      },
      {
        label: '作業情報管理',
        adminOnly: '',
        items: [
          { label: '月間作業予定', route: 'MonthlyAnnualWorkScheduleList' },
          { label: '年間予定作業情報', route: 'CustomerBuildingList' },
          { label: '臨時作業情報', route: 'TemporaryWorkList' },
        ],
      },
      {
        label: '見積書管理',
        visible: SHOW_ESTIMATE_MENU,
        adminOnly: '',
        notificationKey: 'estimateApprovalPending',
        items: [
          { label: '見積書一覧', route: 'EstimateList' },
          {
            label: '承認待ち一覧',
            route: 'EstimateApprovalPendingList',
            adminOnly: true,
            notificationKey: 'estimateApprovalPending'
          },
          {
            label: '新規作成（縦型）',
            route: 'EstimateCreate',
            query: { layout: 'portrait_temporary' },
          },
          {
            label: '新規作成（横型・詳細）',
            route: 'EstimateCreate',
            query: { layout: 'landscape_detailed' },
          },
        ],
      },
      {
        label: '送付表作成',
        adminOnly: '',
        items: [
          { label: '業者別作業予定表', route: 'MonthlyWorkVendorList' },
          { label: '顧客用予定表発行', route: 'NonCreateCalendarBuildingList' },
          { label: '顧客用予定表一覧', route: 'CustomerCalendarList' },
        ],
      },
      {
        label: '請求書管理',
        adminOnly: true,
        items: [
          { label: '顧客金額情報', route: 'CustomerMoneyConditions' },
          { label: '請求書発行', route: 'NonBillingList' },
          { label: '請求書一覧', route: 'BillingList' },
          // { label: '年間作業予定請求', route: 'MonthlyWorkInvoiceBuildingList' },
          // { label: '臨時作業請求', route: 'MonthlyInvoiceTemporaryWorkList' },
        ],
      },
      {
        label: 'システム運用',
        systemAdminOnly: true,
        items: [
          { label: '入金確認データリセット', route: 'CustomerPaymentConfirmReset' },
        ],
      },
    ]);

    const userRole = computed(() => store.state.userRole);
    const isAdmin = computed(() => userRole.value === '999' || userRole.value === '0');
    const isLoggedIn = computed(() => store.state.isLoggedIn);
    const approvalPendingCountRefreshVersion = computed(
      () => store.state.approvalPendingCountRefreshVersion
    );
    const approvalPendingBadgeText = computed(() =>
      approvalPendingCount.value > 99 ? '99+' : String(approvalPendingCount.value)
    );
    const isSeedSystemAdmin = computed(() => {
      return isLoggedIn.value &&
        String(userRole.value) === '999' &&
        localStorage.getItem('uid') === 'admin@example.com';
    });

    const canAccessMeasureSystem = computed(() => {
      if (!isLoggedIn.value) return false;

      // Vue reactivity trigger for login/role updates
      const currentRoleValue = userRole.value;
      const rawRolesBySystem = localStorage.getItem('userRolesBySystem');
      let measureRole = Number.NaN;

      if (rawRolesBySystem) {
        try {
          const parsed = JSON.parse(rawRolesBySystem);
          if (parsed && typeof parsed === 'object') {
            measureRole = Number(parsed.measure);
          }
        } catch (error) {
          console.warn('userRolesBySystem parse error:', error);
        }
      }

      if (!Number.isNaN(measureRole)) {
        return measureRole !== NO_ACCESS_ROLE;
      }

      const currentRole = Number(currentRoleValue ?? localStorage.getItem('userRole'));
      if (Number.isNaN(currentRole)) return true;
      return currentRole !== NO_ACCESS_ROLE;
    });

    const filteredDropdowns = computed(() =>
      dropdowns.value
        .filter((dropdown) => {
          if (!isLoggedIn.value) return false;
          if (dropdown.visible === false) return false;
          if (dropdown.systemAdminOnly && !isSeedSystemAdmin.value) return false;
          return !dropdown.adminOnly || isAdmin.value;
        })
        .map((dropdown) => ({
          ...dropdown,
          items: dropdown.items.filter((item) => !item.adminOnly || isAdmin.value),
        }))
    );

    const fetchApprovalPendingCount = async () => {
      if (!SHOW_ESTIMATE_MENU || !isLoggedIn.value || !isAdmin.value) {
        approvalPendingCount.value = 0;
        isApprovalPendingCountRefreshQueued = false;
        return;
      }
      if (isApprovalPendingCountLoading) {
        isApprovalPendingCountRefreshQueued = true;
        return;
      }

      try {
        isApprovalPendingCountLoading = true;
        const response = await getEstimateApprovalPendingCount();
        if (!SHOW_ESTIMATE_MENU || !isLoggedIn.value || !isAdmin.value) {
          approvalPendingCount.value = 0;
          return;
        }
        const count = Number(response.data?.count);
        approvalPendingCount.value = Number.isFinite(count) && count > 0
          ? Math.floor(count)
          : 0;
      } catch (error) {
        // 通知取得の失敗で通常操作を妨げず、次回更新時に再取得する。
        console.warn('承認待ち件数の取得に失敗しました:', error);
      } finally {
        isApprovalPendingCountLoading = false;
        if (isApprovalPendingCountRefreshQueued) {
          isApprovalPendingCountRefreshQueued = false;
          fetchApprovalPendingCount();
        }
      }
    };

    const stopApprovalPendingPolling = () => {
      if (approvalPendingRefreshTimer !== null) {
        window.clearInterval(approvalPendingRefreshTimer);
        approvalPendingRefreshTimer = null;
      }
    };

    const startApprovalPendingPolling = () => {
      stopApprovalPendingPolling();
      if (!SHOW_ESTIMATE_MENU || !isLoggedIn.value || !isAdmin.value) {
        approvalPendingCount.value = 0;
        return;
      }

      fetchApprovalPendingCount();
      approvalPendingRefreshTimer = window.setInterval(
        fetchApprovalPendingCount,
        APPROVAL_PENDING_REFRESH_INTERVAL_MS
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchApprovalPendingCount();
    };

    watch([isLoggedIn, isAdmin], startApprovalPendingPolling);
    watch(() => route.fullPath, fetchApprovalPendingCount);
    watch(approvalPendingCountRefreshVersion, fetchApprovalPendingCount);

    onMounted(() => {
      startApprovalPendingPolling();
      document.addEventListener('visibilitychange', handleVisibilityChange);
    });

    onBeforeUnmount(() => {
      stopApprovalPendingPolling();
      isApprovalPendingCountRefreshQueued = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });

    const handlePasswordUpdateClick = async (id) => {
        paramsIdToPasswordUpdate.value = id;
        isModalVisible.value = true;

        await nextTick(); // モーダル要素が描画されるのを待つ

        const modalElement = document.querySelector('#passwordUpdateHeaderModal');
        if (modalElement) {
          const modal = Modal.getOrCreateInstance(modalElement); // モーダルを初期化または取得
          modal.show();
        } else {
          console.error('モーダル要素が見つかりません');
        }
      };


    const handleLogout = async () => {
      try {
        await logout();
        store.commit('setLoggedIn', false);
        store.commit('setUserName', null);
        store.commit('setUserRole', null);
        router.push('/login');
      } catch (error) {
        console.error('ログアウトエラー:', error);
      }
    };

    const resolveMeasureSystemBaseUrl = () => {
      const envUrl = process.env.VUE_APP_MEASURE_APP_URL;
      if (envUrl) return envUrl.replace(/\/$/, '');

      const host = window.location.hostname;
      if (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)
      ) {
        return 'http://localhost:8081';
      }

      if (host.includes('stg')) {
        return 'https://stg-measure-report-mng.soei-planning.com';
      }

      return 'https://measure-report-mng.soei-planning.com';
    };

    const moveToMeasureSystem = async () => {
      const measureSystemBaseUrl = resolveMeasureSystemBaseUrl();
      if (!isLoggedIn.value) {
        window.location.href = `${measureSystemBaseUrl}/login`;
        return;
      }

      if (!canAccessMeasureSystem.value) {
        store.dispatch('setFlashMessage', { message: '空気環境システムへのアクセス権限がありません', type: 'warning' });
        return;
      }

      if (isSsoLoading.value) return;

      try {
        isSsoLoading.value = true;
        const response = await createSystemHandoff('measure');
        const handoffToken = response.data?.handoff_token;

        if (!handoffToken) {
          throw new Error('handoff_token is missing');
        }

        const encodedToken = encodeURIComponent(handoffToken);
        window.location.href = `${measureSystemBaseUrl}/sso-entry?sso_token=${encodedToken}`;
      } catch (error) {
        console.error('システム連携エラー:', error);
        store.dispatch('setFlashMessage', { message: '空気環境システムへの連携に失敗しました', type: 'danger' });
      } finally {
        isSsoLoading.value = false;
      }
    };

    const userName = computed(() => store.state.userName);

    return {
      userId,
      userName,
      isLoggedIn,
      isSsoLoading,
      isAdmin,
      approvalPendingCount,
      approvalPendingBadgeText,
      canAccessMeasureSystem,
      dropdowns,
      filteredDropdowns,
      isModalVisible,
      handlePasswordUpdateClick,
      handleLogout,
      moveToMeasureSystem,
      paramsIdToPasswordUpdate,
    };
  },
};
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #000022;
  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

.navbar-container {
  display: flex;
  align-items: center;
  width: 100%;
}

.nav-link, .navbar-brand {
  color: #fff;
  text-decoration: none;
  padding: 10px 15px;
  cursor: pointer;
}

.menu-dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.nav-link:focus, .navbar-brand:focus {
  color: #fff;
}

.nav-link:hover, .navbar-brand:hover {
  color: #f39c12;
}

.navbar-menu {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
}

.nav-item {
  position: relative;
}

.nav-item:hover .dropdown-menu {
  display: block;
}

.dropdown-menu {
  display: none;
  position: absolute;
  background-color: #34495e;
  box-shadow: 0 8px 16px 0 rgba(0,0,0,0.2);
  z-index: 1;
  top: 100%;
  left: 0;
}

.dropdown-item {
  color: #fff;
  padding: 10px 15px;
  text-decoration: none;
  display: block;
}

.dropdown-item--menu-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.approval-notification-badge {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  background: #dc3545;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.dropdown-item:hover {
  background-color: #f39c12;
}

.logout-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  padding: 10px 15px;
}

.logout-button:hover {
  color: #f39c12;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.measure-link-button {
  background: #f39c12;
  border: none;
  color: #fff;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13px;
}

.measure-link-button:hover {
  background: #ffaf35;
}

.measure-link-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
